import {
  computed,
  Injectable,
  linkedSignal,
  resource,
  ResourceRef,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import {
  ETicketType,
  ITicketCategory,
  UNASSIGNED_TICKET_LIST,
} from '@web/dtos';
import { IApiResponse } from '@web/shared-types';
import { firstValueFrom } from 'rxjs';
import { TicketCategoryGateway } from './ticket-category.gateway';

@Injectable({
  providedIn: 'root',
})
export class TicketCategoryStore {
  private readonly ticketCategoryGateway: TicketCategoryGateway =
    new TicketCategoryGateway();

  readonly #ticketCategoriesResource: ResourceRef<
    IApiResponse<ITicketCategory[]> | undefined
  > = resource({
    loader: (): Promise<IApiResponse<ITicketCategory[]>> => {
      return firstValueFrom(this.ticketCategoryGateway.get());
    },
  });

  readonly isLoading: Signal<boolean> = computed((): boolean =>
    this.#ticketCategoriesResource.isLoading()
  );
  readonly ticketCategories: Signal<ITicketCategory[]> = computed(
    (): ITicketCategory[] =>
      this.#ticketCategoriesResource.hasValue()
        ? (this.#ticketCategoriesResource.value()?.data ?? [])
        : []
  );

  readonly writableTicketCategories: WritableSignal<ITicketCategory[]> =
    linkedSignal((): ITicketCategory[] =>
      this.ticketCategories().map(
        (ticketCategory: ITicketCategory): ITicketCategory => ({
          ...ticketCategory,
        })
      )
    );
  readonly sortedDirectTicketCategories: Signal<ITicketCategory[]> = computed(
    (): ITicketCategory[] =>
      this.sortedTicketCategories().filter(
        (category: ITicketCategory) =>
          category.ticketType === ETicketType.Direct
      )
  );

  readonly sortedRelationTicketCategories: Signal<ITicketCategory[]> = computed(
    (): ITicketCategory[] =>
      this.sortedTicketCategories().filter(
        (category: ITicketCategory) =>
          category.ticketType === ETicketType.Relation
      )
  );

  readonly sortedTicketCategories: Signal<ITicketCategory[]> = computed(() =>
    [...this.writableTicketCategories()].sort((a, b) => a.position - b.position)
  );

  readonly connectedDropListIds: Signal<string[]> = computed(() => [
    UNASSIGNED_TICKET_LIST,
    ...this.writableTicketCategories().map(
      category => `category-list-${category.id}`
    ),
  ]);

  public readonly ticketCategoriesToBeDeleted: WritableSignal<string[]> =
    signal([]);

  public readonly hasChanges: Signal<boolean> = computed(() =>
    this.ticketCategoryPageDirtyMark()
  );

  public readonly ticketCategoryPageDirtyMark: WritableSignal<boolean> =
    signal(false);

  public async save(
    ticketCategories: ITicketCategory[]
  ): Promise<IApiResponse<ITicketCategory[]>> {
    const result: IApiResponse<ITicketCategory[]> = await firstValueFrom(
      this.ticketCategoryGateway.save(ticketCategories)
    );

    if (result.data) {
      this.#ticketCategoriesResource.update(() => result);
    }

    return result;
  }

  public async deleteMarkedCategories(): Promise<void> {
    const ticketCategoryIds: string[] = [...this.ticketCategoriesToBeDeleted()];

    for (const ticketCategoryId of ticketCategoryIds) {
      await this.delete(ticketCategoryId);
      this.ticketCategoriesToBeDeleted.update((remainingIds: string[]) =>
        remainingIds.filter(
          (remainingId: string): boolean => remainingId !== ticketCategoryId
        )
      );
    }
  }

  public markForDeletion(ticketCategoryId: string): void {
    this.ticketCategoriesToBeDeleted.update((ticketCategoryIds: string[]) =>
      ticketCategoryIds.includes(ticketCategoryId)
        ? ticketCategoryIds
        : [...ticketCategoryIds, ticketCategoryId]
    );
  }

  public clearMarkedForDeletion(): void {
    this.ticketCategoriesToBeDeleted.set([]);
  }

  public reset(): void {
    this.writableTicketCategories.set(
      this.ticketCategories().map(
        (ticketCategory: ITicketCategory): ITicketCategory => ({
          ...ticketCategory,
        })
      )
    );
    this.clearMarkedForDeletion();
    this.ticketCategoryPageDirtyMark.set(false);
  }

  public reload(): void {
    this.#ticketCategoriesResource.reload();
  }

  public async delete(ticketCategoryId: string): Promise<void> {
    await firstValueFrom(this.ticketCategoryGateway.delete(ticketCategoryId));
  }
}
